import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import type { MetricCatalogItem } from "@/metrics/configure"

import type { WorkspaceSettings } from "@/types_api/backend/transport/workspace_settings"
import type { ConversationMetricRule } from "@/types/api/ConversationMetricRule"

import { normalizeConversationMetricIds } from "../utils/conversationMetricIdentity"
import {
  getConversationMetricsConfigurationSnapshot,
  hasConversationMetricsConfigurationChanges,
} from "../utils/conversationMetricsConfigurationRules"

type ConversationMetricsWorkspaceSettings = Partial<WorkspaceSettings>

// Stable identity for callers that do not pass a catalog, so memoized values
// below keep their references across renders.
const EMPTY_METRICS: MetricCatalogItem[] = []

export function useConversationMetricsConfigurationState(
  workspaceSettings?: ConversationMetricsWorkspaceSettings | null,
  metrics: MetricCatalogItem[] = EMPTY_METRICS,
) {
  const [tempSelectedMetrics, setTempSelectedMetrics] = useState<string[]>([])
  const [savedMetrics, setSavedMetrics] = useState<string[]>([])
  const [tempRules, setTempRules] = useState<ConversationMetricRule[]>([])
  const [savedRules, setSavedRules] = useState<ConversationMetricRule[]>([])
  const [isConfigureMetricsModalOpen, setIsConfigureMetricsModalOpen] =
    useState(false)
  const [justSaved, setJustSaved] = useState(false)
  const [isSavingChanges, setIsSavingChanges] = useState(false)

  const workspaceSettingsSnapshot = useMemo(
    () => getConversationMetricsConfigurationSnapshot(workspaceSettings),
    [workspaceSettings],
  )
  const workspaceDefaultMetrics = useMemo(
    () =>
      normalizeConversationMetricIds(
        workspaceSettingsSnapshot.savedMetrics,
        metrics,
      ),
    [metrics, workspaceSettingsSnapshot.savedMetrics],
  )
  const workspaceRules = useMemo(
    () =>
      workspaceSettingsSnapshot.savedRules.map((rule) => ({
        ...rule,
        metrics: normalizeConversationMetricIds(rule.metrics ?? [], metrics),
      })),
    [metrics, workspaceSettingsSnapshot.savedRules],
  )

  // A catalog or settings refetch returns new object identities even when
  // nothing changed. Hydrate only when the settings response itself changes,
  // or when there is nothing to clobber, so a background refetch never
  // discards unsaved edits.
  const lastHydratedWorkspaceSettingsRef = useRef(workspaceSettings)
  const suppressHydrationRef = useRef(false)

  useEffect(() => {
    if (justSaved) {
      setJustSaved(false)
      return
    }

    if (isSavingChanges) {
      return
    }

    const workspaceSettingsChanged =
      lastHydratedWorkspaceSettingsRef.current !== workspaceSettings

    if (suppressHydrationRef.current) {
      if (!workspaceSettingsChanged) {
        return
      }
      suppressHydrationRef.current = false
    }

    const hasEdits = hasConversationMetricsConfigurationChanges({
      savedMetrics,
      savedRules,
      tempSelectedMetrics,
      tempRules,
    })

    if (!workspaceSettingsChanged && hasEdits) {
      return
    }

    lastHydratedWorkspaceSettingsRef.current = workspaceSettings
    setTempSelectedMetrics(workspaceDefaultMetrics)
    setSavedMetrics(workspaceDefaultMetrics)
    setTempRules(workspaceRules)
    setSavedRules(workspaceRules)
  }, [
    isSavingChanges,
    justSaved,
    workspaceSettings,
    workspaceDefaultMetrics,
    workspaceRules,
    savedMetrics,
    savedRules,
    tempRules,
    tempSelectedMetrics,
  ])

  const hasUnsavedChanges = useCallback(
    () =>
      hasConversationMetricsConfigurationChanges({
        savedMetrics,
        savedRules,
        tempSelectedMetrics,
        tempRules,
      }),
    [savedMetrics, savedRules, tempSelectedMetrics, tempRules],
  )

  const handleToggleMetric = useCallback(
    (metricName: string, isSelected: boolean) => {
      if (isSelected) {
        setTempSelectedMetrics((previous) =>
          previous.includes(metricName) ? previous : [...previous, metricName],
        )
        return
      }

      setTempSelectedMetrics((previous) =>
        previous.filter((id) => id !== metricName),
      )
    },
    [],
  )

  const handleRemoveMetric = useCallback((metricId: string) => {
    setTempSelectedMetrics((previous) =>
      previous.filter((id) => id !== metricId),
    )
  }, [])

  const handleDiscardChanges = useCallback(() => {
    setTempSelectedMetrics(savedMetrics)
    setTempRules(savedRules)
  }, [savedMetrics, savedRules])

  const openConfigureMetricsModal = useCallback(() => {
    setIsConfigureMetricsModalOpen(true)
  }, [])

  const closeConfigureMetricsModal = useCallback(() => {
    setIsConfigureMetricsModalOpen(false)
  }, [])

  const handleCancelMetrics = useCallback(() => {
    setTempSelectedMetrics([...workspaceDefaultMetrics])
    setIsConfigureMetricsModalOpen(false)
  }, [workspaceDefaultMetrics])

  const markMetricsSaved = useCallback(() => {
    setSavedMetrics([...tempSelectedMetrics])
    setJustSaved(true)
    suppressHydrationRef.current = true
  }, [tempSelectedMetrics])

  const markAllChangesSaved = useCallback(() => {
    setSavedMetrics([...tempSelectedMetrics])
    setSavedRules([...tempRules])
    setJustSaved(true)
    suppressHydrationRef.current = true
  }, [tempRules, tempSelectedMetrics])

  const beginSavingChanges = useCallback(() => {
    setIsSavingChanges(true)
  }, [])

  const finishSavingChanges = useCallback(() => {
    setIsSavingChanges(false)
  }, [])

  return {
    beginSavingChanges,
    closeConfigureMetricsModal,
    finishSavingChanges,
    handleCancelMetrics,
    handleDiscardChanges,
    handleRemoveMetric,
    handleToggleMetric,
    hasUnsavedChanges,
    isConfigureMetricsModalOpen,
    isSavingChanges,
    markAllChangesSaved,
    markMetricsSaved,
    openConfigureMetricsModal,
    savedMetrics,
    savedRules,
    setTempRules,
    tempRules,
    tempSelectedMetrics,
  }
}
